import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { notify } from "@/components/common/Notification";
import { DynamicModuleService } from "@/services/APIs";
import { Popconfirm } from "antd";
import {
  Plus,
  Calendar as CalendarIcon,
  Edit2,
  Trash,
  Users,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import MMNavigationRoutes from "../../utils/navigationRoutes";

interface Event {
  id: string;
  eventname?: string;
  category?: string;
  eventtype?: string;
  startdate?: string;
  enddate?: string;
  overallcapacity?: number;
  location?: string;
  organization?: string;
  attendees?: number;
}

const EventsListPage = () => {
  const navigate = useNavigate();
  const schemaName = "Event Information";
  const page = 1;
  const pageSize = 100; // Get more items for card view

  // Fetch events data
  const {
    data: eventsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["eventsData", schemaName, page, pageSize],
    queryFn: async () => {
      const response = await DynamicModuleService.getGridCoByConfig(
        schemaName ?? "",
        page,
        pageSize,
        [
          "id",
          "eventname",
          "category",
          "eventtype",
          "startdate",
          "enddate",
          "overallcapacity",
        ],
        {}
      );
      // Handle response - it might be an array or object with results
      if (response?.results && Array.isArray(response.results)) {
        return response.results;
      } else if (Array.isArray(response)) {
        return response;
      }
      return [];
    },
    enabled: !!schemaName,
  });

  const handleDelete = async (id: string) => {
    try {
      await DynamicModuleService.deleteItem(schemaName ?? "", id);
      // notify.success("Event deleted successfully");
      refetch();
    } catch (error) {
      console.error("Delete error:", error);
      notify.error("Failed to delete event");
    }
  };

  const handleEdit = (id: string, eventname?: string) => {
    const query = eventname ? `?fname=${encodeURIComponent(eventname)}` : "";
    navigate("/mm" + MMNavigationRoutes.EDIT_EVENT(id) + query);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return "N/A";
    }
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const events: Event[] = eventsData ?? [];
  const now = new Date();
  const expiredEvents = events.filter(
    event => event.enddate && new Date(event.enddate) < now
  );
  const activeEvents = events.filter(
    event => !event.enddate || new Date(event.enddate) >= now
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Events</h1>
          <p className="mt-1 text-muted-foreground">
            Manage all platform events
          </p>
        </div>
        <Button
          onClick={() => navigate("/mm" + MMNavigationRoutes.CREATE_EVENT)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Event
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading events...</div>
        </div>
      ) : events.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">No events found</div>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Active / Upcoming Events</h2>
            {activeEvents.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No active or upcoming events
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {activeEvents.map(event => (
                  <Card
                    key={event.id}
                    className="transition-shadow hover:shadow-lg"
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">
                          {event.eventname ?? "Untitled Event"}
                        </CardTitle>
                        {event.eventtype && (
                          <Badge variant="default">{event.eventtype}</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {event.startdate && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>Start: {formatDate(event.startdate)}</span>
                        </div>
                      )}
                      {event.enddate && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>End: {formatDate(event.enddate)}</span>
                        </div>
                      )}
                      {event.category && (
                        <div className="pt-2">
                          <p className="text-sm font-medium text-foreground">
                            {event.category}
                          </p>
                        </div>
                      )}
                      {event.overallcapacity && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{event.overallcapacity} Overall Capacity</span>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          className="h-9 flex-1 text-sm"
                          onClick={() => handleEdit(event.id, event.eventname)}
                        >
                          <Edit2 className="mr-1 h-4 w-4" />
                          Edit
                        </Button>
                        <Popconfirm
                          title="Are you sure you want to delete this event?"
                          onConfirm={() => handleDelete(event.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            variant="outline"
                            className="h-9 flex-1 text-sm text-red-600 hover:text-red-700"
                          >
                            <Trash className="mr-1 h-4 w-4" />
                            Delete
                          </Button>
                        </Popconfirm>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Expired Events</h2>
            {expiredEvents.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No expired events
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {expiredEvents.map(event => (
                  <Card
                    key={event.id}
                    className="transition-shadow hover:shadow-lg"
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">
                          {event.eventname ?? "Untitled Event"}
                        </CardTitle>
                        <Badge variant="secondary">Expired</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {event.startdate && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>Start: {formatDate(event.startdate)}</span>
                        </div>
                      )}
                      {event.enddate && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>End: {formatDate(event.enddate)}</span>
                        </div>
                      )}
                      {event.category && (
                        <div className="pt-2">
                          <p className="text-sm font-medium text-foreground">
                            {event.category}
                          </p>
                        </div>
                      )}
                      {event.overallcapacity && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{event.overallcapacity} Overall Capacity</span>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          className="h-9 flex-1 text-sm"
                          onClick={() => handleEdit(event.id, event.eventname)}
                        >
                          <Edit2 className="mr-1 h-4 w-4" />
                          Edit
                        </Button>
                        <Popconfirm
                          title="Are you sure you want to delete this event?"
                          onConfirm={() => handleDelete(event.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            variant="outline"
                            className="h-9 flex-1 text-sm text-red-600 hover:text-red-700"
                          >
                            <Trash className="mr-1 h-4 w-4" />
                            Delete
                          </Button>
                        </Popconfirm>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EventsListPage;
